import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Schedule as ScheduleIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon
} from '@mui/icons-material';
import { SchedulerConfig } from '@/types/smsScheduler';
import { formatErrorForDisplay } from '@/utils/errorUtils';

interface SchedulerConfigTableProps {
  configurations: SchedulerConfig[];
  loading?: boolean;
  error?: string | null;
  onEdit: (config: SchedulerConfig) => void;
  onDelete: (config: SchedulerConfig) => void;
  onTrigger: (configId: number) => void;
}

const SchedulerConfigTable: React.FC<SchedulerConfigTableProps> = ({
  configurations,
  loading = false,
  error = null,
  onEdit,
  onDelete,
  onTrigger
}) => {
  const formatTime = (timeString: string): string => {
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours, 10);
      const minute = parseInt(minutes, 10);
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
    } catch {
      return timeString;
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {formatErrorForDisplay(error)}
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading scheduler configurations...
        </Typography>
      </Box>
    );
  }

  if (configurations.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <ScheduleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No Scheduler Configurations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create your first scheduler configuration to get started
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Schedule Name</TableCell>
            <TableCell align="center">Days Before RED</TableCell>
            <TableCell align="center">Time</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell align="center">Created By</TableCell>
            <TableCell align="center">Last Updated</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {configurations.map((config) => (
            <TableRow key={config.id} hover>
              <TableCell>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {config.schedule_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {config.id}
                  </Typography>
                </Box>
              </TableCell>
              
              <TableCell align="center">
                <Chip
                  label={`${config.days_before_red} days`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </TableCell>
              
              <TableCell align="center">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ScheduleIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">
                    {formatTime(config.time_of_day)}
                  </Typography>
                </Box>
              </TableCell>
              
              <TableCell align="center">
                <Chip
                  icon={config.is_active ? <ActiveIcon /> : <InactiveIcon />}
                  label={config.is_active ? 'Active' : 'Inactive'}
                  color={config.is_active ? 'success' : 'default'}
                  size="small"
                />
              </TableCell>
              
              <TableCell align="center">
                <Typography variant="body2" color="text.secondary">
                  {config.created_by || 'System'}
                </Typography>
              </TableCell>
              
              <TableCell align="center">
                <Typography variant="caption" color="text.secondary">
                  {formatDate(config.updated_at)}
                </Typography>
              </TableCell>
              
              <TableCell align="center">
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <Tooltip title="Edit Configuration">
                    <IconButton
                      size="small"
                      onClick={() => onEdit(config)}
                      color="primary"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Manual Trigger">
                    <IconButton
                      size="small"
                      onClick={() => onTrigger(config.id)}
                      color="secondary"
                    >
                      <PlayIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Delete Configuration">
                    <IconButton
                      size="small"
                      onClick={() => onDelete(config)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SchedulerConfigTable;

